import type { Room } from "../schema";
import { mapTaskKey } from "./map-keys";

// Bison Internal (2026-11-02, Elijah): a checklist phase with `internal: true`
// holds the actions Bison takes on what the supplier submits. Staff and the
// hub's skills (provisioning token) see it; for anyone else the API removes
// the phase from the room JSON and its keys from the state sidecar, and
// refuses writes to them. Hiding it in the renderer alone would still ship it
// to the supplier's browser.

type MapPhase = Extract<Room["pages"][number]["widgets"][number], { type: "map" }>["props"]["phases"][number];

/** Internal phases always render last, whatever order the room stores. */
export function internalLast<T extends { internal?: boolean }>(phases: T[]): T[] {
    return [...phases.filter((p) => !p.internal), ...phases.filter((p) => p.internal)];
}

/** State keys (`<widgetId>:<taskId>`) of every step in an internal phase. */
export function internalTaskKeys(room: Room): Set<string> {
    const keys = new Set<string>();
    for (const page of room.pages) {
        for (const w of page.widgets ?? []) {
            if (w.type !== "map") continue;
            w.props.phases.forEach((phase: MapPhase, phaseIdx: number) => {
                if (!phase.internal) return;
                phase.tasks.forEach((task, taskIdx) => keys.add(mapTaskKey({ widgetId: w.id, task, phaseIdx, taskIdx })));
            });
        }
    }
    return keys;
}

/** The room as a supplier may receive it: internal phases removed. */
export function withoutInternal(room: Room): Room {
    return {
        ...room,
        pages: room.pages.map((page) => ({
            ...page,
            widgets: (page.widgets ?? []).map((w) =>
                w.type === "map" ? { ...w, props: { ...w.props, phases: w.props.phases.filter((p) => !p.internal) } } : w,
            ),
        })),
    };
}
